import pluralize from 'pluralize';

import { Icons } from '@cast/design-system';

import { useConfirmationDialog } from './useConfirmationDialog';

type ConfirmationDialogProps = {
  willBeEnabled: boolean;
  onConfirm?: () => void;
  onCancel?: () => void;
};

type MultiDialogProps = {
  willBeEnabled: boolean;
  workloadsCount: number;
  onConfirm?: () => void;
  onCancel?: () => void;
};

export const useWorkloadOptimizationChangeConfirmationDialogs = () => {
  const { openConfirmationDialog, closeConfirmationDialog } =
    useConfirmationDialog();

  const openOptimizationDialog = ({
    name,
    bodyEnabled,
    bodyDisabled,
    testId,
    willBeEnabled,
    onConfirm,
    onCancel,
  }: {
    name: string;
    bodyEnabled: string;
    bodyDisabled: string;
    testId: string;
    willBeEnabled: boolean;
    onConfirm?: () => void;
    onCancel?: () => void;
  }) => {
    openConfirmationDialog({
      onCancel,
      confirmButtonProps: {
        onClick: () => {
          onConfirm?.();
          closeConfirmationDialog();
        },
      },
      name,
      title: willBeEnabled
        ? 'Enable automatic optimization?'
        : 'Disable automatic optimization?',
      body: willBeEnabled ? bodyEnabled : bodyDisabled,
      confirmBtnLabel: willBeEnabled ? 'Enable' : 'Disable',
      cancelBtnLabel: 'Cancel',
      variant: willBeEnabled ? 'check' : 'notification',
      icon: willBeEnabled ? <Icons.ToggleRight /> : <Icons.ToggleLeft />,
      testId,
    });
  };

  const openOverrideOptimizationStatusConfirmationDialog = (
    props: ConfirmationDialogProps
  ) => {
    openOptimizationDialog({
      ...props,
      name: 'Override',
      bodyEnabled:
        'This will turn on automated resource adjustments for this workload. It will no longer follow the scaling policy.',
      bodyDisabled:
        'This will stop automated resource adjustments for this workload. It will no longer follow the scaling policy.',
      testId: 'override-workload-optimization-confirmation-dialog',
    });
  };

  const openRevertToPolicyStatusConfirmationDialog = (
    props: ConfirmationDialogProps
  ) => {
    openOptimizationDialog({
      ...props,
      name: 'Revert to policy status',
      bodyEnabled:
        'This will turn on automated resource adjustments for this workload.',
      bodyDisabled:
        'This will stop automated resource adjustments for this workload.',
      testId: 'revert-workload-optimization-confirmation-dialog',
    });
  };

  const openChangeOptimizationStatusOfMultipleWorkloadsConfirmationDialog = ({
    workloadsCount,
    ...props
  }: MultiDialogProps) => {
    openOptimizationDialog({
      ...props,
      name: `${workloadsCount} ${pluralize('workload', workloadsCount)}`,
      bodyEnabled:
        'Selected workloads will have automatic optimization enabled. Policy settings will be overridden for workloads with policies set to off.',
      bodyDisabled:
        'Selected workloads will have automatic optimization disabled. Policy settings will be overridden for workloads with policies set to on.',
      testId: 'workloads-switch-optimization-confirmation-dialog',
    });
  };

  return {
    openOverrideOptimizationStatusConfirmationDialog,
    openRevertToPolicyStatusConfirmationDialog,
    openChangeOptimizationStatusOfMultipleWorkloadsConfirmationDialog,
  };
};
